import { round } from 'vend-number'
import uuid from '@vend/vend-uuid'
import { ModalFactory } from '@vend/vend.ui'
import { ApiRetailerPaymentType, ApiTransaction } from '@vend/types'

import { $q } from '../../ng-compat/imports'
import { SaleTransformer } from '../../factories/sale-transformer'
import { RegisterManager } from '../../managers/register-manager'
import { ReduxCurrentSale } from '../../state/slices/currentSale/currentSaleSlice'
import { lineItemsAdapter } from '../../state/slices/currentSale/lineItemEntityAdapter'
import { IntegratedPaymentModalController } from './integrated-payment/integrated-payment-modal/integrated-payment-modal.controller'
import { SwiperPaymentModalController as ReactSwiperPaymentModalController } from './swiper-payment-modal/react/swiper-payment-modal.controller'
import {
  AcceptStep,
  PAYMENT_STEPS,
} from './integrated-payment/integrated-payment-steps.constant'

import cashPaymentModalTemplate from './cash-payment-modal/cash-payment-modal.html'
import cashDiscountingModalTemplate from './cash-discounting-modal/cash-discounting-modal.html'
import integratePaymentModalTemplate from './integrated-payment/integrated-payment-modal/integrated-payment-modal.html'
import partialAuthModalTemplate from './partial-auth-modal/partial-auth-modal.html'
import partialPaymentModalTemplate from './partial-payment-modal/partial-payment-modal.html'
import reactSwiperPaymentModalTemplate from './swiper-payment-modal/react/swiper-payment-modal.html'
import lspayPaymentModalTemplate from './lspay-payment-modal/lspay-payment-modal.html'
import lspayEcomRefundModalTemplate from './lspay-ecom-refund-modal/lspay-ecom-refund-modal.html'
import payLinkLimitModalTemplate from './pay-link-limit-modal/pay-link-limit-modal.html'
import lspayRecoveryModalTemplate from './lspay-recovery-modal/lspay-recovery-modal.html'
import { LSPAYModalOptions } from './lspay-payment-modal/helpers/lspay.types'
import { PartialPaymentTypeName } from './partial-payment-modal/PartialPaymentModal'
import {
  CashPaymentInformation,
  RoundedNumber,
} from './cash-payment-modal/CashPaymentModal'
import { PaymentData } from './integrated-payment/integrated-payment-modal/IntegratedPaymentModal'
import { LSPayEcomRefundModalOptions } from './lspay-ecom-refund-modal/helpers/types'

/**
 * Service class for payment modals.
 *
 * @class PaymentModalService
 */
export class PaymentModalService {
  constructor(
    private Modal: ModalFactory,
    private saleTransformer: SaleTransformer,
    private registerManager: RegisterManager
  ) {
    'ngInject'
  }

  /**
   * Open the modal responsible for handling the partial payment.
   *
   * @method partialPayment
   *
   * @param  {Object} paymentType
   *         The paymentType to use for the partial payment.
   *
   * @param  {Number} availableCredit
   *         The available credit for the payment type.
   *
   * @return {ModalInstance} The instance of the modal.
   */
  public partialPayment(
    paymentTypeName: PartialPaymentTypeName,
    availableCredit: number
  ) {
    return this.Modal<boolean>({
      controller: 'partialPaymentModalController',
      controllerAs: '$ctrl',
      template: partialPaymentModalTemplate,
      paymentTypeName,
      availableCredit,
    }).then(modal => modal.closed)
  }

  /**
   * Opens the Swiper payment modal.
   *
   * @method swiperPayment
   *
   * @param {Object} params
   *
   *        @param {Number} params.amount
   *               The payment amount.
   *
   *        @param {Object} paymentType
   *               The paymentType to use for the payment
   *
   * @return {Promise} Resolved once the payment process is successful.
   */
  public swiperPayment({
    amount,
    paymentType,
    originalRegisterSalePaymentId,
    currency,
  }: {
    amount: number
    paymentType: ApiRetailerPaymentType
    originalRegisterSalePaymentId: string
    currency: string
  }) {
    return $q((resolve, reject) => {
      this.Modal<ApiTransaction>({
        controller: ReactSwiperPaymentModalController,
        controllerAs: '$ctrl',
        template: reactSwiperPaymentModalTemplate,
        amount,
        currency,
        retailerPaymentType: paymentType,
        registerId: this.registerManager.getActiveRegister().id,
      })
        .then(modal => modal.closed)
        .then(transaction => {
          if (transaction) {
            resolve(transaction)
          } else {
            reject()
          }
        })
    })
  }

  /**
   * Opens the Lightspeed payment modal.
   *
   * @method lspayPayment
   *
   * @param {Object} params
   *
   *        @param {Number} amount
   *               The payment amount.
   *        @param {String} currency
   *               The store's currency.
   *        @param {String} outletId
   *               The id of the outlet.
   *        @param {ApiRegister} register
   *               The current register.
   *        @param {String} registerSaleId
   *               The sale id.
   *        @param {ApiRetailerPaymentType} retailerPaymentType
   *               The current retailer payment type.
   *        @param {String} transactionId
   *               The register sale payment id.
   *        @param {Boolean|undefined} originalSalePaidByOtherPT
   *               True if a payment type other than Lspay was used, false otherwise.
   *        @param {String[]|undefined} originalRegisterSalePaymentIds
   *               The original register sale payment id.
   *        @param {String|undefined} paymentIntentId
   *              The payment intent id.
   *        @param {Boolean |undefined} isRecovery
   *             True if the payment is a recovery.
   *
   * @return {Promise} Resolved once the payment process is successful.
   */
  public lspayPayment(paymentOptions: LSPAYModalOptions) {
    return $q((resolve, reject) => {
      this.Modal<ApiTransaction>({
        ...paymentOptions,
        controller: 'lspayPaymentModalController',
        controllerAs: '$ctrl',
        template: lspayPaymentModalTemplate,
      })
        .then(modal => modal.closed)
        .then(transaction => {
          if (transaction) {
            resolve(transaction)
          } else {
            reject()
          }
        })
    })
  }

  /**
   * Opens the respective payment modal based on the payment type id.
   *
   * @method integratedPayment
   *
   * @param {Object} params
   *
   *        @param {PaymentType} params.paymentType
   *               The payment type of the payment.
   *
   *        @param {String|Number} params.amountTendered
   *               The amount tendered for the selected payment.
   *
   *        @param {Sale} params.sale
   *               The current sale.
   *
   *        @param {String} params.registerId
   *               The id of the current register.
   *
   *        @param {Array} params.externalTransactionIds
   *               The id of the original register sale external transaction ids. Required for refunds only.
   *
   * @return {Promise} The result of the integrated payment.
   */
  public integratedPayment({
    currency,
    retailerPaymentTypeId,
    paymentType,
    amountTendered,
    sale,
    registerId,
    externalTransactionIds,
    registerSalePaymentIds,
  }: {
    currency: $TSFixMe
    retailerPaymentTypeId: string
    paymentType: ApiRetailerPaymentType
    amountTendered: string | number
    sale: ReduxCurrentSale
    registerId: string
    externalTransactionIds: $TSFixMe[]
    registerSalePaymentIds: string[]
  }) {
    const paymentData: PaymentData = {
      currency,
      retailerPaymentTypeId,
      amount: round(amountTendered, 2), //! For some integrated payment providers (SmartPay), it is important to have two digits behind the decimal points
      paymentType,
      registerId,
      externalTransactionIds,
      registerSalePaymentIds,
      paymentId: uuid(),
    }

    const saleData = this.saleTransformer.toClientApi(
      sale,
      lineItemsAdapter.getSelectors().selectAll(sale.lineItems)
    )

    return this.Modal<AcceptStep>({
      controller: IntegratedPaymentModalController,
      controllerAs: '$ctrl',
      template: integratePaymentModalTemplate,
      paymentData,
      saleData,
    })
      .then(modal => modal.close)
      .then(eventData => {
        // Handle the modal being closed via $state change
        if (!eventData) {
          return $q.reject({
            step: PAYMENT_STEPS.DISMISSED,
          })
        } else {
          return {
            ...eventData,
            payment_id: paymentData.paymentId,
          }
        }
      })
  }

  /**
   * Open the modal responsible for handling quick cash payments.
   *
   * @method cashPayment
   *
   * @param  {RoundedNumber} roundedAmountTendered
   *         The rounded amount tendered to pass through if paying the full amount tendered.
   *
   * @param  {RoundedNumber} roundedBalance
   *         The rounded total balance to calculate quick cash options.
   *
   * @return {ModalInstance} The instance of the modal.
   */
  public cashPayment(
    currencySymbol: string,
    roundedAmountTendered: RoundedNumber,
    roundedBalance: RoundedNumber,
    rounding: string
  ) {
    return this.Modal<CashPaymentInformation>({
      controller: 'cashModalController',
      controllerAs: '$ctrl',
      template: cashPaymentModalTemplate,
      currencySymbol,
      roundedAmountTendered,
      roundedBalance,
      rounding,
    })
  }

  /**
   * Open the modal responsible for handling cash discounted payments.
   *
   * @method cashDiscountingPayment
   *
   * @param {Object} params
   *
   *        @param  {VendNumber} params.amountTendered
   *                The amount tendered to pass through if paying the full amount tendered.
   *
   *        @param  {VendNumber} params.balance
   *                The outstanding balance.
   *
   *        @param  {VendNumber} params.cashDiscountedBalance
   *                The cash discounted outstanding balance.
   *
   * @return {ModalInstance} The instance of the modal.
   */
  public cashDiscountingPayment({
    amountTendered,
    balance,
    cashDiscountedBalance,
  }: {
    amountTendered: number | string
    balance: number | string
    cashDiscountedBalance: number | string
  }) {
    return this.Modal<number>({
      controller: 'cashDiscountingModalController',
      controllerAs: '$ctrl',
      template: cashDiscountingModalTemplate,
      amountTendered,
      balance,
      cashDiscountedBalance,
    }).then(modal => modal.close)
  }

  /**
   * Open the modal responsible for handling the partial auth.
   *
   * @method partialAuth
   *
   * @param  {Number} approvedAmount
   *         The amount of partial approved.
   *
   * @return {ModalInstance} The instance of the modal.
   */
  public partialAuth(approvedAmount: number) {
    return this.Modal<boolean>({
      controller: 'partialAuthModalController',
      controllerAs: '$ctrl',
      template: partialAuthModalTemplate,
      approvedAmount,
    }).then(modal => modal.closed)
  }

  /**
   * Opens the Lightspeed ecom refund modal.
   *
   * @method lspayEcomRefund
   *
   * @param {Object} params
   *
   *        @param {Number} params.amount
   *               The refund amount
   *        @param {String} params.originalSaleId
   *               The id of the original sale that has been synced to X-series from an ecom sale
   *        @param {String} params.registerSalePaymentId
   *              The id of the current payment (uuid)
   *        @param {String} params.registerId
   *               The id of the register
   *
   *
   * @return {Promise} Resolved once the refund is successful.
   */
  public lspayEcomRefund(paymentOptions: LSPayEcomRefundModalOptions) {
    return $q((resolve, reject) => {
      this.Modal<ApiTransaction>({
        ...paymentOptions,
        controller: 'lspayEcomRefundModalController',
        controllerAs: '$ctrl',
        template: lspayEcomRefundModalTemplate,
      })
        .then(modal => modal.closed)
        .then(transaction => {
          if (transaction) {
            resolve(transaction)
          } else {
            reject()
          }
        })
    })
  }

  /**
   * Open the modal responsible show the amount limitaion lspay pay link.
   *
   * @method payLinkLimit
   *
   * @param  {Number} amount
   *         The amount of pay link.
   *
   * @return {ModalInstance} The instance of the modal.
   */
  public payLinkLimit(amount: number) {
    return this.Modal<boolean>({
      controller: 'payLinkLimitModalController',
      controllerAs: '$ctrl',
      template: payLinkLimitModalTemplate,
      amount,
    }).then(modal => modal.closed)
  }

  /**
   * Open the modal responsible to initiate LSPay payment recovery.
   *
   * @method lspayRecovery
   *
   * @return {ModalInstance} The instance of the modal.
   */
  public lspayRecovery(saleId: string) {
    return this.Modal<boolean>({
      controller: 'lspayRecoveryModalController',
      controllerAs: '$ctrl',
      template: lspayRecoveryModalTemplate,
      saleId,
    }).then(modal => modal.closed)
  }
}
